import { imagePath } from "../../../../utils/imagePath"

const AnalyticsData =  {
    slug: 'analytics',
    sectionTitle: 'Introduction',
    blocks: [
      {
        type: 'text',
        content: [
          'The Analytics Dashboard is designed to provide a complete overview of user activity, engagement, and geographical distribution across all available experiences. It helps stakeholders understand how users interact with the platform and supports data-driven decision-making.',
          'The dashboard displays analytics for a selected time range and allows filtering by experience type.',
        ],
      },
      {
        type: 'image',
        src: imagePath?.AnalyticsIntroduction,
        alt: 'Dashboard',
      },
    ],
    subSections: [
      {
        title: 'Analytics Summary Header ',
        blocks: [
          {
            type: 'text',
            content: [
              'The Analytics summary header appears at the top of the dashboard and provides a concise overview of overall platform performance for the selected reporting period. This section is designed to give users an immediate understanding of key engagement indicators without requiring detailed analysis.',
              'The header includes the report title, reporting duration, filter controls, and a row of summary metric cards.',
            ],
          },
          {
            type: 'text',
            content: ['**Summary Metric Cards**'],
          },
          {
            type: 'text',
            content: [
              'The summary metrics are displayed as individual cards arranged horizontally. Each card presents a specific performance indicator along with an icon and a prominent value for quick visibility.',
            ],
          },
          {
            type: 'text',
            content: ['**Total Views**'],
          },
          {
            type: 'text',
            content: [
              ' This metric represents the total number of times experiences were viewed by users during the selected reporting period. It provides an overall measure of platform reach and content visibility.',
            ],
          },
          {
            type: 'text',
            content: ['**Total Clicks**'],
          },
          {
            type: 'text',
            content: [
              'This metric indicates the total number of user interaction events, such as clicks or selections, across all experiences. It reflects how actively users are engaging with the platform content.',
            ],
          },
          {
            type: 'text',
            content: ['**Average Time Spent**'],
          },
          {
            type: 'text',
            content: [
              'This metric shows the average duration users spend during a session. It helps assess how engaging the experiences are and whether users remain active for meaningful periods.',
            ],
          },
          {
            type: 'text',
            content: ['**Average Scenes per Session**'],
          },
          {
            type: 'text',
            content: [
              ' This metric represents the average number of scenes or screens a user navigates through in a single session. It indicates the depth of user interaction within an experience.',
            ],
          },
          {
            type: 'text',
            content: ['**Bounce Rate**'],
          },
          {
            type: 'text',
            content: [
              ' This metric reflects the percentage of users who exit the platform after interacting with only a single screen or experience. A higher bounce rate may indicate that users are not finding the content engaging or relevant.',
            ],
          },
          {
            type: 'text',
            content: ['**Active Projects**'],
          },
          {
            type: 'text',
            content: [
              '  This metric displays the number of projects that are currently live and actively tracked by the analytics system. It helps users understand the scale of active analytics coverage.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.AnalyticsSummryHeader,
            alt: 'Dashboard',
          },
        ],
      },
      {
        title: 'Experience Overview',
        blocks: [
          {
            type: 'text',
            content: [
              'The Experience Overview section provides a focused summary of performance and engagement metrics for individual experiences available on the platform. This section allows stakeholders to compare how different experiences perform in terms of visibility, interaction, and user attention.',
              'The layout is designed to present experience-level insights in a compact and easily readable format.',
            ],
          },
          {
            type: 'text',
            content: ['**Experience Cards**'],
          },
          {
            type: 'text',
            content: [
              'Each experience is displayed inside a separate card. These cards visually group all relevant metrics for a single experience, making comparison simple and intuitive.',
            ],
          },
          {
            type: 'text',
            content: ['**Air Purifier Experience**'],
          },
          {
            type: 'text',
            content: [
              'The Air Purifier card represents analytics data related to the Air Purifier experience.',
              'The Views metric indicates how many times this experience has been accessed by users within the selected reporting period. This reflects the reach and visibility of the experience.',
              'The Clicks metric represents the number of user interactions performed within the experience. This helps measure how actively users engage with the content after opening it.',
              'The Average Time metric shows the typical duration users spend interacting with this experience during a session. A higher value suggests stronger engagement and interest.',
            ],
          },
          {
            type: 'text',
            content: ['**New Promotion Experience**'],
          },
          {
            type: 'text',
            content: [
              'The New Promotion card presents analytics data for the New Promotion experience.',
              'The Views metric shows how often this experience was opened by users.',
              'The Clicks metric indicates how many interaction events occurred within this experience, reflecting user involvement.',
              'The Average Time metric represents the average session duration for this experience, helping assess how long users remain engaged before exiting.',
            ],
          },
          {
            type: 'text',
            content: ['**Platform Distribution Chart**'],
          },
          {
            type: 'text',
            content: [
              'Below the experience cards, a circular chart visually represents the distribution of users based on the platform they used to access the experiences.',
              'The chart segments indicate the proportion of users accessing the platform via iOS, Android, and Web.',
              'The Android segment occupies the largest portion of the chart, showing that the majority of users access experiences using Android devices. The iOS segment represents a smaller but significant portion of users. The Web segment indicates no recorded usage for the selected reporting period.',
              'This visualization helps teams understand device preferences and prioritize platform-specific optimization and testing efforts.',
            ],
          },
          {
            type: 'text',
            content: ['**Data Interpretation and Usage**'],
          },
          {
            type: 'text',
            content: [
              'The Experience Overview section allows users to quickly assess which experiences attract more views, generate more interaction, and retain user attention longer.',
              'By comparing experience cards side by side, stakeholders can identify high-performing experiences and those that may require content improvements, UX adjustments, or promotional support.',
              'Platform distribution insights support strategic decisions related to development focus, device compatibility, and resource allocation.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.ExperienceOverview,
            alt: 'Dashboard',
          },
        ],
      },
      {
        title: 'Experience Traffic',
        blocks: [
          {
            type: 'text',
            content: [
              'The Experience Traffic section presents a visual representation of how user activity varies over time for the selected experiences. This section helps stakeholders understand daily engagement trends and identify patterns in user behavior.',
              'The data is displayed using a line chart, which allows easy comparison of activity levels across different days within the selected time range.',
            ],
          },
          {
            type: 'text',
            content: ['**Experience Traffic Header**'],
          },
          {
            type: 'text',
            content: [
              'The section is titled Experience Traffic, clearly indicating that the information shown relates to user visits and viewing activity over time.',
              'A time-range selector is available in the header, allowing users to define the period for which traffic data is displayed. Selecting a different range updates the chart dynamically. An additional action icon is provided to support features such as exporting the chart or expanding the view for detailed analysis.',
            ],
          },
          {
            type: 'text',
            content: ['**Traffic Line Chart**'],
          },
          {
            type: 'text',
            content: [
              'The line chart visually tracks how many users viewed the experience on each day within the selected period.',
              'The horizontal axis represents the timeline, displaying calendar dates in chronological order. This helps users understand how engagement changes over time.',
              'The vertical axis represents the number of users who viewed the experience on a given day. Higher points on the line indicate increased user activity, while lower points indicate reduced engagement.',
              'A continuous green line connects daily data points, clearly illustrating fluctuations in experience traffic. Peaks in the line represent days with higher user participation, while dips indicate periods of lower activity.',
            ],
          },
          {
            type: 'text',
            content: ['**Data Interpretation**'],
          },
          {
            type: 'text',
            content: [
              'The Experience Traffic chart enables users to identify trends such as high-engagement days, low-activity periods, and sudden spikes or drops in traffic.',
              'Spikes may indicate the impact of promotions, releases, or marketing efforts, while declines may suggest reduced interest or external factors affecting usage.',
              'By analyzing these patterns, stakeholders can evaluate the effectiveness of campaigns, understand user behavior over time, and plan improvements or interventions accordingly.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.ExperienceTraffic,
            alt: 'Dashboard',
          },
        ],
      },
      {
        title: 'New vs Returning',
        blocks: [
          {
            type: 'text',
            content: [
              'The New vs Returning section provides insight into user retention by showing the proportion of first-time users compared to users who return to the platform. This section helps stakeholders understand whether the platform is primarily attracting new users or successfully retaining existing ones.',
              'The information is presented using a circular donut chart, which visually divides users into two categories for quick interpretation.',
            ],
          },
          {
            type: 'text',
            content: ['**New Users**'],
          },
          {
            type: 'text',
            content: [
              'The New Users segment represents users who are accessing the platform for the first time within the selected reporting period. A higher share of new users indicates effective user acquisition through promotions, campaigns, or organic discovery.',
            ],
          },
          {
            type: 'text',
            content: ['**Returning Users**'],
          },
          {
            type: 'text',
            content: [
              'The Returning Users segment represents users who have previously accessed the platform and have returned during the selected reporting period. This reflects user satisfaction, engagement, and the platform’s ability to retain users over time.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.NewReturning,
            alt: 'Dashboard',
          },
        ],
      },
      {
        title: 'Country Based Analytics',
        blocks: [
          {
            type: 'text',
            content: [
              'The Country Based Analytics section provides insight into user activity by displaying the distribution of sessions across different countries. This section helps stakeholders understand the geographical reach of the platform and identify regions with higher or lower user engagement.',
              'The data is presented in a ranked list format, making it easy to compare activity levels between countries.',
            ],
          },
          {
            type: 'text',
            content: ['**Top Countries List**'],
          },
          {
            type: 'text',
            content: [
              'The section highlights the countries from which users are accessing the platform most frequently. Each country is displayed along with its national flag, session count, and a horizontal progress bar.',
              'The length of the progress bar visually represents the relative number of sessions generated from each country. Countries with higher engagement appear with longer bars, allowing quick visual comparison.',
            ],
          },
          {
            type: 'text',
            content: ['**Session Count Display**'],
          },
          {
            type: 'text',
            content: [
              'Alongside each country, the total number of sessions is displayed. A session represents a user’s active interaction period with the platform. This metric helps measure engagement intensity from each geographic region.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.CountryBasedAnalysis,
            alt: 'Dashboard',
          },
        ],
      },
      {
        title: 'Location Based Analytics',
        blocks: [
          {
            type: 'text',
            content: [
              'The Location Based Analytics section displays user engagement data grouped by specific locations such as cities or regions. It helps stakeholders understand how different locations contribute to overall platform usage.',
              'The section presents a tabular view showing locations along with their corresponding number of views and engagement percentage. A progress bar visually represents the level of activity for each location, allowing quick comparison between regions.',
              'A sorting option is provided to view the data based on criteria such as views. Action icons allow users to access additional details or perform further analysis for a selected location.',
              'This section is useful for identifying high-performing locations and regions with lower engagement, supporting location-specific decision-making and optimization.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.LocationBasedAnalysis,
            alt: 'Dashboard',
          },
        ],
      },
      {
        title: 'Interaction Tracking',
        blocks: [
          {
            type: 'text',
            content: [
              'The Interaction Tracking section provides detailed insights into how users interact with specific elements within a project. This section helps analyze user behavior by tracking the scenes users view and the buttons or links they interact with the most.',
              'The data is organized into two panels to clearly separate viewing behavior from interaction behavior.',
            ],
          },
          {
            type: 'text',
            content: ['**Top Scenes**'],
          },
          {
            type: 'text',
            content: [
              'The Top Scenes panel displays the scenes that users viewed most recently or most frequently within a selected project.',
              'Each scene is listed with a horizontal progress bar that visually represents the number of sessions in which the scene was viewed. Longer bars indicate higher user engagement with that scene.',
              'The session count shown alongside each scene represents how many user sessions included that scene. This helps identify which parts of the experience attract the most attention and which scenes may be less engaging.',
              'A project selector allows users to switch between different projects and view scene-level analytics specific to the selected project.',
            ],
          },
          {
            type: 'text',
            content: ['**Top Clicked Buttons and Links**'],
          },
          {
            type: 'text',
            content: [
              'The Top Clicked Buttons and Links panel shows the interactive elements that users clicked or activated most often within the selected project.',
              'Each button or link is displayed with a progress bar representing interaction frequency. This visual representation helps quickly identify the most-used controls or content elements.',
              'The session count indicates how many sessions included interaction with the specific button or link. This information is useful for evaluating call-to-action effectiveness and understanding user navigation behavior.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.InteractionUi,
            alt: 'Dashboard',
          },
          {
            type: 'text',
            content: ['**Interaction with UI Elements**'],
          },
          {
            type: 'text',
            content: [
              'The Interaction with UI Elements section provides insights into how users engage with different scenes and interface elements within a selected project. This section helps understand both user presence across scenes and the level of interaction those scenes generate.',
              'The information is divided into two complementary panels to clearly distinguish between scene presence and user click activity.',
            ],
          },
          {
            type: 'text',
            content: ['**Scene Presence**'],
          },
          {
            type: 'text',
            content: [
              'The Scene Presence panel shows how often users visited or spent time in specific scenes within the selected project.',
              'Each scene is listed with a horizontal progress bar representing the number of sessions in which the scene appeared. A higher session count indicates that users frequently reached or remained in that scene.',
              'This view helps identify which scenes are most commonly accessed and which scenes receive limited user attention. A project selector allows switching between different projects to view scene presence data specific to each experience.',
            ],
          },
          {
            type: 'text',
            content: ['**Top Scene Clickthroughs**'],
          },
          {
            type: 'text',
            content: [
              'The Top Scene Clickthroughs panel displays how actively users interacted with UI elements within each scene.',
              'Each scene is shown with a progress bar indicating the total number of clicks performed on UI elements present in that scene. Higher click counts suggest stronger user interaction and engagement with the content or controls available in that scene.',
              'This information is useful for evaluating the effectiveness of buttons, links, and other interactive elements placed within each scene.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.InteractionUi,
            alt: 'Dashboard',
          },
        ],
      },
      {
        title: 'User Segmentation',
        blocks: [
          {
            type: 'text',
            content: [
              'The User Segmentation section provides a detailed, session-level view of individual user interactions within the platform. This section enables stakeholders to analyze user behavior by breaking down activity data for each user session.',
              'The information is presented in a tabular format, allowing easy comparison and filtering of user activity records.',
            ],
          },
          {
            type: 'text',
            content: ['**User List Table**'],
          },
          {
            type: 'text',
            content: [
              'Each row in the table represents a single user session. The table captures key behavioral attributes that describe how a user interacted with the experience during that session.',
              'A selection checkbox is provided for each row to enable bulk actions or focused analysis on selected user records. A star icon allows users to mark specific sessions as important or noteworthy for further review.',
            ],
          },
          {
            type: 'text',
            content: ['**User Identification**'],
          },
          {
            type: 'text',
            content: [
              'The User column displays a unique user identifier along with the associated country indicator. This identifier is anonymized to protect user privacy while still allowing session-level analysis. The country indicator helps correlate user behavior with geographic origin.',
            ],
          },
          {
            type: 'text',
            content: ['**Exit Scene**'],
          },
          {
            type: 'text',
            content: [
              'The Exit Scene column shows the scene where the user ended their session. This information is useful for identifying drop-off points in the user journey and understanding where users commonly exit the experience.',
            ],
          },
          {
            type: 'text',
            content: ['**Actions Performed**'],
          },
          {
            type: 'text',
            content: [
              'The Actions column indicates the total number of interactions performed by the user during the session. Actions may include clicks, selections, button presses, or other tracked interactions. This helps measure how actively the user engaged with the experience.',
            ],
          },

          {
            type: 'text',
            content: ['**Scenes Visited**'],
          },
          {
            type: 'text',
            content: [
              'The Scenes column displays the number of scenes the user navigated through during the session. This metric reflects the depth of exploration and user movement within the experience.',
            ],
          },
          {
            type: 'text',
            content: ['**Session Duration**'],
          },
          {
            type: 'text',
            content: [
              'The Duration column represents the total time spent by the user during the session. Longer durations generally indicate higher engagement, while shorter durations may suggest quick exits or limited interaction.',
            ],
          },
          {
            type: 'text',
            content: ['**Session Date and Time**'],
          },
          {
            type: 'text',
            content: [
              'The Date column shows when the session occurred. This allows stakeholders to correlate user behavior with specific time periods, campaigns, or events.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.UserSegmentation,
            alt: 'Dashboard',
          },
        ],
      },
      {
        title: 'Sales & Deployment Analysis',
        blocks: [
          {
            type: 'text',
            content: [
              'The Sales & Deployment Analysis section provides insights into how deployed experiences are being used and engaged with by sales executives and deployment locations. This section helps track performance at an operational level by linking user engagement metrics with specific personnel and deployment sites.',
              'The data is organized into two distinct panels to separate sales performance analysis from deployment location analysis.',
            ],
          },
          {
            type: 'text',
            content: ['**Sales Executives Analysis**'],
          },
          {
            type: 'text',
            content: [
              'The Sales Executives Analysis panel presents engagement data associated with individual sales executives.',
              'Each row represents a sales executive and displays their name along with the assigned region. The views column shows how many times experiences associated with that executive were accessed. The engagement column represents the average time users spent interacting with the experience delivered by that executive.',
              'A trends indicator is displayed using a small line graph to visually represent changes in engagement or activity over time. Action icons allow users to drill down into detailed analytics or review interaction data specific to that executive.This panel helps evaluate individual sales performance, engagement effectiveness, and regional activity patterns.',
            ],
          },
          {
            type: 'text',
            content: ['**Fuel Station Analysis**'],
          },
          {
            type: 'text',
            content: [
              'The Fuel Station Analysis panel displays analytics data related to deployed experiences at specific fuel station locations.',
              'Each row corresponds to a fuel station and includes the station name and region. The views column shows how often the experience was accessed at that location. The clicks column indicates the number of user interactions performed within the experience.',
              'A trends indicator visually represents changes in engagement activity over time, enabling quick assessment of performance consistency or fluctuation. Action icons provide access to deeper analytics or session-level details for each deployment location.',
              'This panel supports evaluation of deployment effectiveness and helps identify high-performing and low-performing locations.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.SalesDeployment,
            alt: 'Dashboard',
          },
        ],
      },
      {
        title: 'Interaction Heatmap & Behavior Analysis',
        blocks: [
          {
            type: 'text',
            content: [
              'This screen provides a visual and analytical view of how users interact with an experience. It combines interaction metrics, heatmap visualization, and behavior filters to help teams understand where users click, how long they stay, and where they disengage.',
              'The view is primarily used for UX analysis, interaction optimization, and drop-off investigation.',
            ],
          },
          {
            type: 'text',
            content: ['**Interaction Summary Metrics**'],
          },
          {
            type: 'text',
            content: [
              'At the top of the screen, key interaction indicators summarize overall user behavior for the selected time range.',
            ],
          },
          {
            type: 'text',
            content: ['**Total Clicks Recorded**'],
          },
          {
            type: 'text',
            content: [
              'This value represents the total number of user click interactions captured across the experience. It reflects overall interaction volume.',
            ],
          },
          {
            type: 'text',
            content: ['**Average Time**'],
          },
          {
            type: 'text',
            content: [
              ' This indicates the average time users spent interacting with the experience. It helps measure engagement depth and content effectiveness.',
            ],
          },
          {
            type: 'text',
            content: ['**U-Turns**'],
          },
          {
            type: 'text',
            content: [
              'This metric represents instances where users reversed their navigation path, indicating confusion, hesitation, or difficulty in progressing.',
            ],
          },
          {
            type: 'text',
            content: ['**Drop-Off**'],
          },
          {
            type: 'text',
            content: [
              ' This value shows the percentage of users who exited the experience before completion. A higher drop-off suggests usability or engagement issues.',
            ],
          },
          {
            type: 'text',
            content: ['**Errors**'],
          },
          {
            type: 'text',
            content: [
              ' This displays the number of interaction errors encountered by users, such as invalid clicks or failed actions. A value of zero indicates stable interaction flow.',
            ],
          },
          {
            type: 'text',
            content: ['**Experience Selection and Filters**'],
          },
          {
            type: 'text',
            content: [
              ' Below the metrics, controls are provided to manage the data scope.',
              'The experience indicator shows which experience is currently being analyzed. The date range selector allows filtering interaction data to a specific time period. An additional filter option enables refined analysis based on interaction criteria or user behavior patterns.',
            ],
          },
          {
            type: 'text',
            content: ['**Heatmap Visualization Area**'],
          },
          {
            type: 'text',
            content: [
              ' The central area displays the experience with heatmap overlays applied.',
              'Colored regions indicate interaction intensity. Warm colors represent areas with high user interaction. Cool colors represent low or minimal interaction zones.',
              'This visualization helps quickly identify which UI elements or objects attract the most user attention and which areas are ignored.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.HeatmapBehaviour,
            alt: 'Dashboard',
          },
        ],
      },
      {
        title: 'Error Tracking & Debugging',
        blocks: [
          {
            type: 'text',
            content: [
              'The Error Tracking & Debugging section provides visibility into user frustration signals and technical issues occurring within the platform. This section helps teams identify usability problems, system errors, and stability issues that may negatively impact user experience.',
              'The information is presented through timebased charts to support trend analysis and root-cause investigation.',
            ],
          },
          {
            type: 'text',
            content: ['**Rage Clicks and U-Turns**'],
          },
          {
            type: 'text',
            content: [
              'The Rage Clicks and UTurns panel focuses on behavioral indicators that suggest user confusion or frustration.',
              'Rage clicks represent repeated rapid clicks in the same area, often indicating that an element is not responding as expected or is unclear to the user. U turns represent situations where users reverse their navigation path, suggesting hesitation, confusion, or difficulty in progressing through the experience.',
              'The line chart displays how these behaviors change over time for the selected period. Peaks in the chart highlight days with higher levels of user frustration, while lower values indicate smoother user interaction.',
              'Filters allow users to view this data across all projects or narrow it down for focused analysis.',
            ],
          },
          {
            type: 'text',
            content: ['**Scenes with Errors or Issues**'],
          },
          {
            type: 'text',
            content: [
              'The Scenes with Errors or Issues panel tracks technical problems encountered by users while interacting with different scenes.',
              'This chart displays multiple error types, such as script errors, runtime errors, bad requests, and internal server errors. Each error type is represented by a distinct line, allowing teams to compare trends and identify recurring issues.',
              'The timeline shows when errors occurred, helping teams correlate issues with deployments, updates, or external factors. Filters enable selection of specific projects or scenes to isolate problem areas.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.ErrorTracking,
            alt: 'Dashboard',
          },
        ],
      },
      {
        title: 'Conversion & Goal Tracking ',
        blocks: [
          {
            type: 'text',
            content: [
              'The Conversion & Goal Tracking section provides insights into how users progress through defined goals within an experience. This section helps measure the effectiveness of user journeys, identify drop-off points, and evaluate how well the experience drives desired actions.',
              'The data is presented using funnel-style visualizations to clearly show step-by-step user progression.',
            ],
          },
          {
            type: 'text',
            content: ['**Sign-Up Conversion**'],
          },
          {
            type: 'text',
            content: [
              'The Sign-Up Conversion panel illustrates the user journey from initial experience access to successful form submission.',
              'The funnel begins with users who viewed the opening experience, representing overall reach. It then shows how many users clicked and opened the initial interactive tile, followed by how many users clicked the sign-up button. The final step shows users who successfully submitted the form with valid input.',
              'Each stage visually represents the proportion of users who progressed to the next step. Decreasing bar heights indicate user drop-off at each stage, helping identify where users disengage.',
            ],
          },
          {
            type: 'text',
            content: ['**Desktop vs Mobile Conversion**'],
          },
          {
            type: 'text',
            content: [
              'The Desktop vs Mobile Conversion panel compares goal completion behavior across device types.',
              'Separate visual indicators show how users on mobile and desktop devices progress through the same conversion steps. This comparison highlights differences in engagement and completion rates between platforms.',
              'The visualization helps identify whether device-specific issues, usability challenges, or performance limitations are affecting conversions.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.ConversionTracking,
            alt: 'Dashboard',
          },
        ],
      },
      {
        title: 'User Behavior & Experience – NPS Survey Explanation',
        blocks: [
          {
            type: 'text',
            content: [
              'The User Behavior & Experience section presents feedback insights collected through a Net Promoter Score (NPS) survey. This section helps evaluate overall user satisfaction and perception of the platform based on direct user responses.',
              'The data displayed reflects aggregated feedback from users across selected projects.',
            ],
          },
          {
            type: 'text',
            content: ['**NPS Survey Overview**'],
          },
          {
            type: 'text',
            content: [
              'The NPS score represents the overall sentiment of users toward the platform. It is derived from survey responses that measure the likelihood of users recommending the experience to others.',
              'The displayed NPS value indicates the balance between positive and negative user sentiment. A negative score suggests that a higher number of users are dissatisfied compared to those who are highly satisfied.',
              'A status indicator is shown alongside the score to provide a quick qualitative assessment of user sentiment. In this case, the status highlights that improvement is required.',
            ],
          },
          {
            type: 'text',
            content: ['**Survey Responses**'],
          },
          {
            type: 'text',
            content: [
              'The total number of responses represents how many users participated in the survey. This provides context for the reliability and scale of the feedback data.',
            ],
          },
          {
            type: 'text',
            content: ['**Response Categories**'],
          },
          {
            type: 'text',
            content: [
              'User responses are grouped into three standard NPS categories and displayed using horizontal bars for easy comparison.',
            ],
          },
          {
            type: 'text',
            content: ['**Promoters**'],
          },
          {
            type: 'text',
            content: [
              ' These users are highly satisfied and likely to recommend the platform. They represent strong approval and positive user experience.',
            ],
          },
          {
            type: 'text',
            content: ['**Passives**'],
          },
          {
            type: 'text',
            content: [
              'These users are moderately satisfied but not strongly enthusiastic. They are unlikely to actively promote the platform but are also less likely to discourage others.',
            ],
          },
          {
            type: 'text',
            content: ['**Detractors**'],
          },
          {
            type: 'text',
            content: [
              'These users are dissatisfied and may negatively influence perception of the platform. A higher number of detractors indicates areas where the experience may be falling short.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.Userbehaviour,
            alt: 'Dashboard',
          },
        ],
      },
    ],
}
  
export default AnalyticsData